export const rotatingCapabilities = [
  "Enterprise IT Support",
  "Active Directory",
  "Windows Server",
  "Cisco Networking",
  "Compliance Support",
  "AWS and Azure"
];

export const navItems = [
  ["Command Center", "home"],
  ["Operator Profile", "about"],
  ["Capabilities", "skills"],
  ["Mission Logs", "projects"],
  ["Product Lab", "software"],
  ["Timeline", "experience"],
  ["Credentials", "credentials"],
  ["Contact", "contact"]
];

export const widgets = [
  ["Current Role", "CSS", "Computer Systems Specialist at Educational Training Resources"],
  ["Asset Scope", "200+", "Devices reviewed through quarterly and annual inventory cycles"],
  ["Security Focus", "NIST", "Compliance, access control, BitLocker, and policy support"],
  ["Systems Stack", "Hybrid", "Windows Server, Cisco, Citrix, AWS, Azure, and VMware"],
  ["Education", "2025", "Cybersecurity B.S. candidate at Full Sail University"]
];

export const skills = [
  {
    title: "Enterprise IT Support",
    signal: "IT",
    description: "Support users, endpoints, and daily operations in an educational and vocational environment.",
    items: ["Hardware troubleshooting", "Software installation", "Device configuration", "Inventory management", "Solution documentation", "Escalation coordination"]
  },
  {
    title: "Networking",
    signal: "NET",
    description: "Work with network infrastructure, protocols, and connectivity issues across managed environments.",
    items: ["Network administration", "Network infrastructure", "Cisco equipment", "LAN/WAN", "DNS", "DHCP", "TCP/IP"]
  },
  {
    title: "Cybersecurity",
    signal: "SEC",
    description: "Support defensive operations through access control, endpoint hardening, risk thinking, and user education.",
    items: ["Incident response", "Access control", "Endpoint hardening", "Penetration testing", "Risk assessments", "Security awareness"]
  },
  {
    title: "Compliance",
    signal: "GRC",
    description: "Translate security requirements into practical procedures, policies, and technical guidance.",
    items: ["NIST 800-53", "PCI DSS", "Security Compliance", "Privacy", "BitLocker", "Policy authorship"]
  },
  {
    title: "Systems and Cloud",
    signal: "SYS",
    description: "Administer and troubleshoot core platforms used by modern IT teams.",
    items: ["Windows Server", "Active Directory", "Citrix", "Linux", "VMware ESXi", "AWS", "Azure"]
  },
  {
    title: "Tools and Automation",
    signal: "OPS",
    description: "Use technical tools, documentation, and scripting to make support work repeatable.",
    items: ["Nessus", "NMAP", "Wireshark", "PowerShell", "Bash", "Git", "Technical documentation"]
  }
];

export const projects = [
  {
    name: "Enterprise Asset Inventory Review",
    status: "Current Role",
    tools: ["Inventory process", "Device records", "Documentation"],
    summary: "Quarterly and annual reviews for 200+ devices across a live educational IT environment.",
    problem: "Accurate device ownership, lifecycle status, and support visibility are essential for reliable operations and compliance readiness.",
    result: "Lead structured inventory reviews that improve accountability, device tracking, and support planning."
  },
  {
    name: "Security Policy and Procedure Support",
    status: "Operational",
    tools: ["NIST 800-53", "Policy docs", "User education"],
    summary: "Policy authorship, technical announcements, and device management procedures supporting cybersecurity compliance.",
    problem: "Security controls only work when users and support teams understand the procedure behind them.",
    result: "Helped turn compliance needs into clear guidance, announcements, and repeatable operational procedures."
  },
  {
    name: "Windows Server and Active Directory Support",
    status: "Hands-on",
    tools: ["Windows Server", "Active Directory", "Citrix"],
    summary: "Support for enterprise systems, users, accounts, and access workflows.",
    problem: "User access and system issues can interrupt daily operations when identity and endpoint workflows are not handled quickly.",
    result: "Supported account, access, and endpoint troubleshooting to keep staff and training operations moving."
  },
  {
    name: "Network and Endpoint Troubleshooting",
    status: "Active",
    tools: ["Cisco", "LAN/WAN", "Wireshark", "NMAP"],
    summary: "Diagnosis of hardware, software, network, and device issues in a production support environment.",
    problem: "Frontline incidents need calm triage across endpoints, connectivity, and user workflow symptoms.",
    result: "Provided frontline support, documented fixes, and coordinated escalations for issues that required deeper resolution."
  },
  {
    name: "Cybersecurity Coursework Labs",
    status: "Academic",
    tools: ["IAM", "Network Security", "Threat Intelligence", "Software Security"],
    summary: "Coursework focused on identity, network defense, compliance, threat intelligence, and secure software practices.",
    problem: "Cybersecurity concepts become stronger when they connect directly to systems, users, and operational risk.",
    result: "Built a foundation that connects academic security frameworks to practical IT support and defense."
  },
  {
    name: "Portfolio Website System",
    status: "Online",
    tools: ["React", "Azure Static Web Apps", "GitHub"],
    summary: "This portfolio, deployed as a cyber operations interface for IT and cybersecurity work.",
    problem: "A technical portfolio should show both personality and operational competence.",
    result: "Built a responsive command-center interface with mission files, credentials, contact flow, and keyboard navigation."
  }
];

export const featuredSoftwareProjects = [
  {
    name: "GiveMeUI",
    code: "DEV-01",
    category: "Developer Tooling",
    status: "Active Development",
    summary: "A local-first developer tool that turns command-line interfaces into approachable, reviewable graphical workflows.",
    vision: "Make powerful terminal tools easier to discover and operate without hiding the commands, arguments, or execution risks underneath the interface.",
    buildFocus: "Command discovery, generated schemas, safe argv-based execution, reusable workflows, project persistence, and optional local AI assistance.",
    tools: ["TypeScript", "React", "Vite", "Node.js", "node-pty", "SQLite"],
    repository: "https://github.com/Senqxi/GIVEMEUI",
    repositoryLabel: "View Repository",
    preview: {
      type: "terminal",
      label: "LOCAL TOOL ADAPTER",
      lines: ["$ givemeui python3 --help", "schema.review: ready", "runner.mode: argv-safe"]
    }
  },
  {
    name: "Monument",
    code: "FIN-02",
    category: "Fintech Research",
    status: "Active Development",
    summary: "A research-first asset intelligence platform for discovering, comparing, simulating, and tracking tokenized real-world assets.",
    vision: "Give everyday users a clearer, more responsible way to understand tokenized assets before making decisions elsewhere.",
    buildFocus: "Asset research briefs, comparison tools, portfolio scenarios, watchlists, educational content, data transparency, and private-beta readiness.",
    tools: ["React", "Vite", "Supabase", "Vitest", "Asset Data", "Responsive UI"],
    repository: "https://github.com/Senqxi/monument-tokenized-asset-managment-app",
    repositoryLabel: "View Repository",
    preview: {
      type: "markets",
      label: "ASSET INTELLIGENCE",
      lines: ["research.mode: active", "portfolio.sim: ready", "watchlist.sync: building"]
    }
  },
  {
    name: "The 19th App",
    code: "GLF-03",
    category: "Golf Media + Community",
    status: "Active Development",
    summary: "A golf-focused product concept bringing course discovery, player performance, social groups, and live golf content into one experience.",
    vision: "Create a modern home for golfers to follow the game, connect with friends, discover courses, and understand their own performance.",
    buildFocus: "Product strategy, mobile experience design, community features, statistics, course discovery, media integration, and App Store preparation.",
    tools: ["Product Strategy", "UX/UI", "iOS Planning", "Golf Data", "Community", "Media"],
    repository: null,
    repositoryLabel: null,
    preview: {
      type: "golf",
      label: "ROUND CONTROL",
      lines: ["course.discovery: planned", "player.stats: designing", "social.feed: prototyping"]
    }
  }
];

export const experience = [
  {
    time: "2024-11",
    role: "Computer Systems Specialist",
    env: "Educational Training Resources - Oneonta, NY",
    range: "November 2024 - Present",
    work: "Provide frontline IT support by diagnosing hardware and software issues, configuring devices, maintaining inventory, documenting solutions, and coordinating escalations.",
    wins: "Lead quarterly and annual asset inventory reviews for 200+ devices while supporting Windows Server, Cisco network equipment, Active Directory, Citrix, AWS, and Azure."
  },
  {
    time: "2023-04",
    role: "Cybersecurity B.S. Candidate",
    env: "Full Sail University - Online",
    range: "April 2023 - December 2025",
    work: "Studying Identity and Access Management, Network Security and Software, Security Compliance and Privacy, Threat Intelligence and Defense, and Software Security.",
    wins: "Built a security foundation around compliance, access control, threat intelligence, and defensive operations."
  },
  {
    time: "2023-04",
    role: "Information Technology Graduate",
    env: "Full Sail University - Online",
    range: "April 2023 - October 2024",
    work: "Completed an Information Technology program focused on systems, networks, troubleshooting, and technical operations.",
    wins: "Earned a 3.2 GPA while building toward a cybersecurity-focused career path."
  },
  {
    time: "2017-04",
    role: "Computer Science Studies",
    env: "The University of Oneonta - Oneonta, NY",
    range: "April 2017 - January 2022",
    work: "Started with a computer science foundation before shifting deeper into cybersecurity and protection of digital systems.",
    wins: "Developed the technical groundwork that later shaped the move into IT and cybersecurity."
  }
];

export const credentials = [
  "Cybersecurity B.S. Candidate",
  "Information Technology B.T.",
  "CompTIA ITF+ Prep",
  "CompTIA Network+ Prep",
  "Tech Assistance Scholarship",
  "New Momentum Scholarship"
];
